import {type ReactNode, useMemo} from 'react'

import {BoardDragProvider} from '../drag/BoardDragProvider'
import {BoardFocusProvider} from '../focus/BoardFocusProvider'
import {BoardReportingProvider} from '../reporting/BoardReportingProvider'
import type {BoardReporting} from '../reporting/types'
import {BoardSelectionProvider} from '../selection/BoardSelectionProvider'
import type {BoardSelectedCard} from '../selection/types'
import type {BoardCell, BoardData, BoardItemDragState, BoardItemsMove, BoardLabels, ItemId} from '../types'
import {createCellItemIndex} from './cell-item-index'
import {composeCellKey} from './cell-key'
import {
  type BoardCardDragPolicy,
  BoardCardDragPolicyContext,
  type BoardConfig,
  BoardConfigContext,
  BoardDataContext,
  type BoardDataContextValue,
  type BoardPaging,
  BoardPagingContext,
} from './contexts'

export type BoardProviderProps<TItem> = BoardPaging &
  BoardReporting & {
    data: BoardData<TItem>
    /** The caller's stable identity for a card. */
    getItemId: (item: TItem) => ItemId
    /** The card's human-readable name, used for accessible names and announcements. */
    getItemLabel: (item: TItem) => string
    /**
     * Whether one card may be dragged, and why not when it may not. Defaults to draggable
     * for every card.
     *
     * The board holds no rule about who may move what: it reads no permission, asks
     * nothing about what kind of thing a card is, and applies nothing of its own on top of
     * the answer. Whatever this returns is the whole of it, so a caller can make any card
     * draggable and any card not.
     *
     * A refusal carries the caller's own reason, which reaches a card through
     * `useBoardCard`'s `dragRefusal`. See {@link BoardItemDragState} for why the answer is
     * one boolean per card rather than one per direction.
     *
     * Called with the caller's own item, resolved from the card's address against the
     * `data` above, so a card the caller's data no longer holds is left draggable rather
     * than refused on its behalf.
     */
    getItemDragState?: (item: TItem) => BoardItemDragState
    /** Every user-visible string. The package ships none of its own. */
    labels: BoardLabels
    /**
     * Whether the caller renders a name for each row. A board with no swimlane has one
     * row that means nothing to the reader, so its cells are named after their column
     * alone. Defaults to `false`.
     *
     * The caller states this rather than the board deriving it from `rows.length`, which
     * would be the single-row special case the contract forbids.
     */
    hasRowHeaders?: boolean
    /**
     * Put both scroll containers back to the start whenever this changes, on both axes
     * and without animating.
     *
     * The board does not restore scroll position and cannot: only the caller knows
     * whether the `data` it just supplied is the same board with another page in it, or a
     * different board entirely. Change this when it is a different board. Leave it
     * undefined and the board never resets.
     */
    resetScrollKey?: string | number
    /**
     * The cards that are selected. Defaults to none.
     *
     * The board holds no selection of its own and derives none, so this is the whole of
     * what is selected: a card is selected because it is in this list and for no other
     * reason. Leave it out and nothing is ever selected, whatever anybody clicks.
     *
     * A card is a cell and an item rather than an item alone, because the same item can
     * sit in two cells on a board whose field takes several values, and a caller acting on
     * a selection has to know which of them a reader meant.
     */
    selectedCards?: readonly BoardSelectedCard[]
    /**
     * Called with what a gesture makes of {@link BoardProviderProps.selectedCards}, for the
     * caller to store and hand back.
     *
     * The board applies nothing itself. Supply no handler and the board has no selection at
     * all, which is the state every board was in before this existed.
     *
     * It is called only when a gesture really did change the selection, so clicking the one
     * card already selected reports nothing.
     */
    onSelectionChange?: (selectedCards: readonly BoardSelectedCard[]) => void
    /**
     * Called when a drop moves a card somewhere it was not, with the cards that moved, the
     * cell they were in and the cell they are going to.
     *
     * **The board performs no write and holds no move of its own.** It calls this and
     * carries on drawing the `data` it was given, so the card stays exactly where it was
     * until you hand back data that says otherwise. Nothing is held optimistically and
     * nothing is queued.
     *
     * A drop that lands in the cell the card came from reports nothing at all, because
     * there is nothing about it for a caller to act on: within one cell the only ordering
     * is the active sort. So is a drop outside every cell, and one on a droppable of the
     * caller's own.
     *
     * Which cards may be moved is stated per card through
     * {@link BoardProviderProps.getItemDragState}, and this holds no rule of its own on top
     * of it.
     */
    onItemsMove?: (move: BoardItemsMove) => void
    children: ReactNode
  }

const NO_SELECTED_CARDS: readonly BoardSelectedCard[] = []

const DRAGGABLE: BoardItemDragState = {draggable: true}

/**
 * What a board that says nothing about dragging answers, which is the board every caller
 * had before this existed. Declared here rather than inline so the config keeps one
 * identity across renders for a caller that supplies nothing.
 */
function everyCardIsDraggable(): BoardItemDragState {
  return DRAGGABLE
}

/**
 * Publishes the board's contexts. Renders no DOM of its own, so a caller is free to
 * put the provider anywhere in its tree and lay the board out however it likes.
 */
export function BoardProvider<TItem>({
  data,
  getItemId,
  getItemLabel,
  getItemDragState = everyCardIsDraggable,
  labels,
  hasRowHeaders = false,
  resetScrollKey,
  selectedCards = NO_SELECTED_CARDS,
  onSelectionChange,
  onItemsMove,
  onLoadMoreItems,
  onLoadMoreColumns,
  onLoadMoreRows,
  onFocusedCardChange,
  onCardDialogChange,
  onDragChange,
  children,
}: BoardProviderProps<TItem>): ReactNode {
  const dataValue = useMemo<BoardDataContextValue>(() => {
    const columnsByKey = new Map(data.columns.map(column => [column.key, column]))
    const rowsByKey = new Map(data.rows.map(row => [row.key, row]))

    const cellsByAddress = new Map<string, BoardCell<unknown>>()
    for (const cell of data.cells) {
      cellsByAddress.set(composeCellKey(cell), cell)
    }

    return {data, columnsByKey, rowsByKey, cellsByAddress, itemIndex: createCellItemIndex(cellsByAddress)}
  }, [data])

  const config = useMemo<BoardConfig>(
    // The accessors are contravariant in `TItem`, so the context holds them widened and
    // the typed hooks narrow them back. The provider is the only place that knows both
    // sides, which is what makes the cast safe.
    () => ({getItemId, getItemLabel, labels, hasRowHeaders, resetScrollKey}) as BoardConfig,
    [getItemId, getItemLabel, labels, hasRowHeaders, resetScrollKey],
  )

  /*
    The caller's answer for one card, resolved from the address the card is drawn at.

    Built here rather than in the card so that `useBoardCard`, the one hook that runs per
    drawn card, subscribes to the three things that can change the answer and to nothing
    else. Reading the config there instead would wake every card whenever a label,
    `getItemLabel`, `hasRowHeaders` or `resetScrollKey` changed identity, none of which a
    card reads.

    The item is resolved through the index rather than passed, because a card is addressed
    by its cell and its id while the caller's predicate takes the caller's own item. The
    selected-card resolver reads the same index: the first card in a cell builds it and
    every card after that is a `Map` lookup, so a drawn cell is scanned once rather than
    once per card in it.
  */
  const {itemIndex} = dataValue
  const dragPolicy = useMemo<BoardCardDragPolicy>(
    () => (cellKey, itemId) => {
      // A card with no id cannot be looked up: an empty id names every such card in the
      // cell, so an answer would as likely be a different card's. It cannot be dragged
      // either way, and no reason for that is the caller's to have given.
      if (itemId === '') return undefined

      const item = itemIndex.itemIn(cellKey, itemId, getItemId as (item: unknown) => ItemId)
      // A card the caller's data no longer holds is left draggable. The caller was never
      // asked about it, so refusing it would be the board deciding.
      if (item === undefined) return undefined

      const dragState = getItemDragState(item as TItem)

      return dragState.draggable ? undefined : dragState.reason
    },
    [itemIndex, getItemId, getItemDragState],
  )

  // Its own context rather than part of the config, because a caller building these
  // inline hands over three new functions on every render, and a card that only reads a
  // label must not re-render for that.
  const paging = useMemo<BoardPaging>(
    () => ({onLoadMoreItems, onLoadMoreColumns, onLoadMoreRows}),
    [onLoadMoreItems, onLoadMoreColumns, onLoadMoreRows],
  )

  return (
    <BoardConfigContext value={config}>
      <BoardPagingContext value={paging}>
        {/*
          Innermost, because the board the focus reducer moves over is built from the
          data and the config above it. It renders no DOM either.
        */}
        <BoardDataContext value={dataValue}>
          {/*
            Below the data and config it is built from, and above everything a card is
            drawn in. It carries one answer per card and nothing else, so a card reads it
            without reading either of them.
          */}
          <BoardCardDragPolicyContext value={dragPolicy}>
            {/*
              Inside the data context for the same reason the focus provider is: a range
              runs over the cards one cell holds, which is a fact stated above it. It
              renders no DOM either.
            */}
            <BoardSelectionProvider selectedCards={selectedCards} onSelectionChange={onSelectionChange}>
              <BoardFocusProvider>
                {/*
                  Above the reporting provider, because the drag report is one of the things
                  the provider renders itself, and below the data context, because resolving
                  where a card was dropped reads the items a cell has loaded.
                */}
                <BoardDragProvider onItemsMove={onItemsMove}>
                  {/*
                    Innermost of all, because what it reports about focus is read from the
                    focus store above it and what it reports about a drag from the drag
                    context. Nothing it mounts draws anything: reporting where focus is must
                    not put a value on screen that every card would have to re-render for.
                  */}
                  <BoardReportingProvider
                    onFocusedCardChange={onFocusedCardChange}
                    onCardDialogChange={onCardDialogChange}
                    onDragChange={onDragChange}
                  >
                    {children}
                  </BoardReportingProvider>
                </BoardDragProvider>
              </BoardFocusProvider>
            </BoardSelectionProvider>
          </BoardCardDragPolicyContext>
        </BoardDataContext>
      </BoardPagingContext>
    </BoardConfigContext>
  )
}
